/**
 * Dogs API (core-api) — client for the dogs DB shared by web, app and Klaviyo.
 *
 * See dogs-api-web.md. Notes that shape this client:
 *  - The user is resolved from the Clerk web session token; there is no
 *    customerId in any path and a user can only ever touch their own dogs.
 *  - The store/country is picked from the request Origin (barkyn.com → PT,
 *    .es → ES, .it → IT, `www.` accepted), which the browser sets for us.
 *    CORS allows only those origins.
 *  - Bodies are JSON, and the API rejects ANY field outside the editable
 *    whitelist with 400 — so writes are filtered through EDITABLE_FIELDS
 *    rather than trusting callers.
 *  - PATCH is NOT partial: the body must carry the full editable state,
 *    including all of REQUIRED_FIELDS. Use `save` rather than `update` so an
 *    edit can't blank a field the form doesn't show.
 *  - Rate limit is 100 req/min per client; callers should cache (see
 *    LoopFeatures.dogs.getAllDogs).
 */
window.BarkynDogsApi = (() => {
  const BASE_URL_PRODUCTION = "https://core-api.barkyn.com";
  const BASE_URL_STAGING = "https://core-api-staging.barkyn.com";

  // Must be present on every create AND update.
  const REQUIRED_FIELDS = ["name", "gender", "life_stage", "weight"];

  // Only these are accepted in POST/PATCH bodies. Anything else — including
  // loop_line_ids and the other server-managed columns — is a 400.
  const EDITABLE_FIELDS = [
    "name",
    "gender",
    "life_stage",
    "size",
    "weight",
    "birthdate",
    "is_birthdate_unknown",
    "breed",
    "breed_id",
    "suggested_breed_id",
    "concern",
    "image_url",
    "image_location",
    "age",
    "is_neutered",
    "neutered_on",
    "has_allergies",
    "additional_information",
    "chip_number",
    "country",
    "scoop_level",
  ];

  class DogsApiError extends Error {
    constructor(message, { status = 0, requestId = null } = {}) {
      super(message);
      this.name = "DogsApiError";
      this.status = status;
      this.requestId = requestId;
    }

    /** The session is gone — the caller should send the user to re-auth. */
    get isUnauthorized() {
      return this.status === 401;
    }

    /** Dog does not exist, or isn't this user's. */
    get isNotFound() {
      return this.status === 404;
    }

    get isRateLimited() {
      return this.status === 429;
    }
  }

  /**
   * Production hosts are the three storefront domains; anything else (preview
   * themes, *.myshopify.com, local) talks to staging. Override with
   * LoopConfig.dogsApi.baseUrl when testing against a specific environment.
   */
  const getBaseUrl = () => {
    const override = window.LoopConfig?.dogsApi?.baseUrl;
    if (override) return override;

    return /(^|\.)barkyn\.(com|es|it)$/.test(window.location.hostname)
      ? BASE_URL_PRODUCTION
      : BASE_URL_STAGING;
  };

  /**
   * Drop everything the API would reject.
   *
   * Nulls are skipped too: GET returns `null` for unset optional fields
   * (concern, image_url, birthdate…) and echoing those back on a PATCH risks a
   * 400 against the string types. Clearing a field is done with `""`.
   */
  const pickEditableFields = (dog = {}) =>
    EDITABLE_FIELDS.reduce((body, field) => {
      const value = dog[field];
      if (value !== undefined && value !== null) body[field] = value;
      return body;
    }, {});

  const request = async (path, { method = "GET", body } = {}) => {
    const token = await window.ClerkUtils.getToken();

    if (!token) {
      throw new DogsApiError("Unauthorized", { status: 401 });
    }

    const headers = { Authorization: `Bearer ${token}` };
    if (body !== undefined) headers["Content-Type"] = "application/json";

    const url = `${getBaseUrl()}${path}`;

    let response;
    try {
      response = await fetch(url, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (error) {
      // fetch only rejects on network/CORS failures — and a CORS rejection is
      // indistinguishable from being offline, so name both.
      console.error(
        `[DogsApi] ${method} ${url} failed before a response (network or CORS).`,
        `CORS allows only https://(www.)barkyn.(com|es|it) — current origin is ${window.location.origin}.`,
        error,
      );
      throw new DogsApiError(`Network error: ${error.message}`);
    }

    // Captured for error reports — the API traces every request by this id.
    const requestId = response.headers.get("x-request-id");

    if (!response.ok) {
      let message = `Dogs API request failed with status ${response.status}`;
      try {
        const errorBody = await response.json();
        if (errorBody?.message) message = errorBody.message;
      } catch (error) {
        // No JSON body — keep the status-based message.
      }

      console.error(
        `[DogsApi] ${method} ${url} → ${response.status} (x-request-id: ${requestId})`,
        message,
      );
      throw new DogsApiError(message, { status: response.status, requestId });
    }

    if (response.status === 204) return null;

    const text = await response.text();
    if (!text.trim()) return null;

    try {
      return JSON.parse(text);
    } catch (error) {
      throw new DogsApiError("Invalid JSON response", { requestId });
    }
  };

  /** Names the required fields a body is missing, for a clear failure. */
  const findMissingRequired = (body) =>
    REQUIRED_FIELDS.filter(
      (field) => body[field] === undefined || body[field] === "",
    );

  /**
   * Fails before the request when a required field is missing, so the form can
   * say which one instead of surfacing a bare 400.
   */
  const assertRequired = (body) => {
    const missing = findMissingRequired(body);
    if (!missing.length) return;

    const error = new DogsApiError(
      `Missing required field(s): ${missing.join(", ")}`,
      { status: 400 },
    );
    error.missingFields = missing;
    throw error;
  };

  return {
    REQUIRED_FIELDS,
    EDITABLE_FIELDS,
    DogsApiError,
    getBaseUrl,
    pickEditableFields,
    findMissingRequired,
    request,

    /** GET /dogs — the signed-in user's dogs, newest first. */
    list: async () => {
      const dogs = await request("/dogs");
      return Array.isArray(dogs) ? dogs : [];
    },

    /**
     * POST /dogs — name, gender, life_stage and weight are all required.
     * Ownership comes from the token.
     */
    create: (dog) => {
      const body = pickEditableFields(dog);
      assertRequired(body);

      return request("/dogs", { method: "POST", body });
    },

    /**
     * PATCH /dogs/:id with an explicit body. The API expects the full editable
     * state — prefer `save`, which builds that from the dog you already have.
     */
    update: (dogId, body) =>
      request(`/dogs/${dogId}`, {
        method: "PATCH",
        body: pickEditableFields(body),
      }),

    /**
     * Applies `changes` on top of the dog's current editable state and PATCHes
     * the result, so an update can never blank a field the caller didn't know
     * about (image_url, scoop_level, breed, …).
     */
    save: (dog, changes = {}) => {
      const body = {
        ...pickEditableFields(dog),
        // Not pickEditableFields: an explicit "" here is how a field is
        // cleared, and that must survive the merge.
        ...changes,
      };
      assertRequired(body);

      return request(`/dogs/${dog.id}`, {
        method: "PATCH",
        body: pickEditableFields(body),
      });
    },

    /** DELETE /dogs/:id — soft-deleted server side. */
    remove: (dogId) => request(`/dogs/${dogId}`, { method: "DELETE" }),

    /**
     * PUT /products/:lineItemId/dogs — sets which dogs belong to a Loop line
     * item. Send the FULL checked set; the API reconciles and `[]` clears all.
     * Read the current state from each dog's loop_line_ids.
     */
    setProductDogs: (lineItemId, dogIds) =>
      request(`/products/${lineItemId}/dogs`, {
        method: "PUT",
        body: { dogIds },
      }),
  };
})();
